import React, {useEffect, useState, useCallback} from 'react'
import {Flex, Select, Box, Text, useMultiStyleConfig} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

const days = Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, '0'))
const currentYear = new Date().getFullYear()
const years = Array.from({length: 100}, (_, i) => String(currentYear - 1 - i))

const BirthdayField = ({value, onChange, customerBirthday, setCustomError}) => {
    const styles = useMultiStyleConfig('Birthday')
    const {formatMessage} = useIntl()

    const [day, setDay] = useState('')
    const [month, setMonth] = useState('')
    const [year, setYear] = useState('')
    const [errors, setErrors] = useState({day: '', month: '', year: ''})

    const monthNames = [
        {
            value: '01',
            label: formatMessage({id: 'addressFields.dobfield.January', defaultMessage: 'January'})
        },
        {
            value: '02',
            label: formatMessage({
                id: 'addressFields.dobfield.February',
                defaultMessage: 'February'
            })
        },
        {
            value: '03',
            label: formatMessage({id: 'addressFields.dobfield.March', defaultMessage: 'March'})
        },
        {
            value: '04',
            label: formatMessage({id: 'addressFields.dobfield.April', defaultMessage: 'April'})
        },
        {
            value: '05',
            label: formatMessage({id: 'addressFields.dobfield.May', defaultMessage: 'May'})
        },
        {
            value: '06',
            label: formatMessage({id: 'addressFields.dobfield.June', defaultMessage: 'June'})
        },
        {
            value: '07',
            label: formatMessage({id: 'addressFields.dobfield.July', defaultMessage: 'July'})
        },
        {
            value: '08',
            label: formatMessage({id: 'addressFields.dobfield.August', defaultMessage: 'August'})
        },
        {
            value: '09',
            label: formatMessage({
                id: 'addressFields.dobfield.September',
                defaultMessage: 'September'
            })
        },
        {
            value: '10',
            label: formatMessage({id: 'addressFields.dobfield.October', defaultMessage: 'October'})
        },
        {
            value: '11',
            label: formatMessage({
                id: 'addressFields.dobfield.November',
                defaultMessage: 'November'
            })
        },
        {
            value: '12',
            label: formatMessage({
                id: 'addressFields.dobfield.December',
                defaultMessage: 'December'
            })
        }
    ]

    useEffect(() => {
        const source = value || customerBirthday
        if (source && source.includes('-')) {
            const [y, m, d] = source.split('-')
            setYear(y || '')
            setMonth(m || '')
            setDay(d || '')
        }
    }, [])

    useEffect(() => {
        if (day && month && year) {
            onChange?.(`${year}-${month}-${day}`)
        } else {
            onChange?.('')
        }
    }, [day, month, year, onChange])

    const validate = (d, m, y) => {
        const filled = [d, m, y].filter(Boolean).length
        const newErrors = {day: '', month: '', year: ''}

        if (filled === 0 || filled === 3) {
            setCustomError?.(false)
            return newErrors
        }
        if (!d)
            newErrors.day = formatMessage({
                id: 'use_address_fields.error.birthDate.This_field_is_required',
                defaultMessage: 'Please enter a valid date.'
            })
        if (!m)
            newErrors.month = formatMessage({
                id: 'use_address_fields.error.birthDate.This_field_is_required',
                defaultMessage: 'Please enter a valid date.'
            })
        if (!y)
            newErrors.year = formatMessage({
                id: 'use_address_fields.error.birthDate.This_field_is_required',
                defaultMessage: 'Please enter a valid date.'
            })

        setCustomError?.(true)
        return newErrors
    }

    const handleDayChange = useCallback(
        (e) => {
            const val = e.target.value
            setDay(val)
            setErrors(validate(val, month, year))
        },
        [month, year]
    )

    const handleMonthChange = useCallback(
        (e) => {
            const val = e.target.value
            setMonth(val)
            setErrors(validate(day, val, year))
        },
        [day, year]
    )

    const handleYearChange = useCallback(
        (e) => {
            const val = e.target.value
            setYear(val)
            setErrors(validate(day, month, val))
        },
        [day, month]
    )

    return (
        <Flex gap={2} direction="row" {...styles.wrapper}>
            <Box {...styles.selectOptionDay}>
                <Select
                    {...styles.select}
                    value={day}
                    onChange={handleDayChange}
                    isInvalid={!!errors.day}
                >
                    <option value="">
                        {formatMessage({id: 'birthdayField.day', defaultMessage: 'Day'})}
                    </option>
                    {days.map((d) => (
                        <option key={d} value={d}>
                            {d}
                        </option>
                    ))}
                </Select>
                {errors.day && <Text {...styles.dobError}>{errors.day}</Text>}
            </Box>

            <Box {...styles.selectOptionMonth}>
                <Select
                    {...styles.select}
                    value={month}
                    onChange={handleMonthChange}
                    isInvalid={!!errors.month}
                >
                    <option value="">
                        {formatMessage({id: 'birthdayField.month', defaultMessage: 'Month'})}
                    </option>
                    {monthNames.map((m) => (
                        <option key={m.value} value={m.value}>
                            {m.label}
                        </option>
                    ))}
                </Select>
                {errors.month && <Text {...styles.dobError}>{errors.month}</Text>}
            </Box>

            <Box {...styles.selectOptionYear}>
                <Select
                    {...styles.select}
                    value={year}
                    onChange={handleYearChange}
                    isInvalid={!!errors.year}
                >
                    <option value="">
                        {formatMessage({id: 'birthdayField.year', defaultMessage: 'Year'})}
                    </option>
                    {years.map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </Select>
                {errors.year && <Text {...styles.dobError}>{errors.year}</Text>}
            </Box>
        </Flex>
    )
}

BirthdayField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    customerBirthday: PropTypes.string,
    setCustomError: PropTypes.func
}

export default BirthdayField
